import "@liveblocks/react-ui/styles.css"
import "./CollaborationRoom.module.css"

import { LiveList, LiveObject, type LsonObject } from "@liveblocks/client"
import {
  LiveblocksProvider,
  RoomContext as LiveblocksRoomContext,
  useClient,
  useIsInsideRoom,
  useMutation as useLiveblocksMutation,
  useStorage,
} from "@liveblocks/react"
import { useLiveblocksExtension } from "@liveblocks/react-tiptap"
import { getDocumentById } from "@prismicio/editor-fields"
import { useOnChange } from "@prismicio/editor-support/React"
import { Document as DocumentT } from "@prismicio/types-internal/lib/content"
import { useMutation } from "@tanstack/react-query"
import type { Content, Extension } from "@tiptap/core"
import { type ReactNode, useCallback, useState } from "react"
import { z } from "zod"

import { queryClient } from "../../app/components/providers/QueryProvider"
import { env } from "../../app/env"
import {
  useCollaborationDataFlag,
  useCollaborationRoomFlag,
} from "../../app/integrations/AmplitudeExperiments"
import { getRepositoryName, useRouteParams } from "../../app/routing"
import { baseUrl, type DocumentVersionStatus } from "../../app/services/core"
import {
  getLiveblocksSessionOptions,
  resolveMentionSuggestions,
  resolveUsers,
} from "../../app/services/liveblocks"
import { useRepository } from "../../app/services/repository"
import { useProfile } from "../../app/services/user"
import {
  type ExistingDocumentData,
  revalidateDocumentMeta,
  saveDocument,
} from "../documentData"
import { useDocumentHasLocalEdits } from "../DocumentHasLocalEditsProvider"
import { useGoToDocument } from "../documentRouting"
import { findDocumentVersion } from "../findDocumentVersion"
import { useCollaborationRoomState } from "./CollaborationRoomStateProvider"
import { CollaborationStateProvider } from "./CollaborationStateProvider"
import { handleDocumentStorageChanges } from "./handleDocumentStorageChanges"
import { HistoryProvider } from "./HistoryProvider"
import { useLiveblocksError } from "./LiveblocksErrorProvider"
import { RoomConnectionObserver } from "./RoomConnectionObserver"
import { wrapWithLiveblocksModel } from "./wrapWithLiveblocksModel"

interface CollaborationRoomProps {
  data: ExistingDocumentData
  children: ReactNode
}

export function CollaborationRoom(props: CollaborationRoomProps) {
  const { data, children } = props

  const { hasLocalEdits } = useDocumentHasLocalEdits()
  const hasCollaborationRoomTurnedOff = useCollaborationRoomFlag() === "off"

  const { authors } = useRepository()
  const { hasError, setError } = useLiveblocksError()

  async function authEndpoint() {
    try {
      return await queryClient.fetchQuery(getLiveblocksSessionOptions())
    } catch (err) {
      setError({
        type: "authentication",
        message: "Failed to authenticate with Liveblocks.",
      })
      throw err
    }
  }

  if (hasLocalEdits || hasCollaborationRoomTurnedOff || hasError) {
    return (
      <CollaborationStateProvider enabled={false}>
        {children}
      </CollaborationStateProvider>
    )
  }

  return (
    <CollaborationStateProvider enabled>
      <LiveblocksProvider
        largeMessageStrategy="split"
        authEndpoint={authEndpoint}
        resolveUsers={({ userIds }) =>
          resolveUsers({ userIds, authors: [aiAuthor, ...authors] })
        }
        resolveMentionSuggestions={({ text }) =>
          resolveMentionSuggestions({ text, authors })
        }
        preventUnsavedChanges
      >
        <SuspenseCollaborationRoom data={data}>
          {children}
        </SuspenseCollaborationRoom>
      </LiveblocksProvider>
    </CollaborationStateProvider>
  )
}

function SuspenseCollaborationRoom(props: CollaborationRoomProps) {
  const { data, children } = props

  const { shortId } = useProfile()
  const client = useClient()
  const roomState = useCollaborationRoomState()

  const roomId = getRoomId(data)

  if (roomState.room?.id !== roomId) {
    roomState.setNewRoom(getNewRoom())
  }

  // Suspense blocking.
  roomState.waitForRoomData?.()

  function getNewRoom() {
    const { version } = data

    const repository = getRepositoryName()

    const releaseId =
      data.version.status === "release" ? data.version.release_id : undefined

    const { room, leave } = client.enterRoom(roomId, {
      // The initialStorage should not be set when the document is published
      // or archived as we do not want to initialize or use Liveblocks storage
      // data for these, only Prismic Data
      initialStorage: isPublishedOrArchived(version.status)
        ? undefined
        : {
            document: wrapWithLiveblocksModel(
              data.document,
            ) as LiveObject<LsonObject>,
            title: data.meta.title,
            status: version.status,
            releaseId: releaseId,
            shortId,
            tags: new LiveList(data.version.tags),
            repository,
            documentId: data.meta.id,
            commitHash: env.PRISMIC_GIT_COMMIT_HASH,
            lastModifiedDate: data.version.last_modified_date.toISOString(),
          },
      initialPresence: {
        fieldId: undefined,
      },
    })

    return { room, leave, data }
  }

  return (
    <LiveblocksRoomContext.Provider value={roomState?.room ?? null}>
      {children}
      <HistoryProvider />
      <RoomConnectionObserver />
    </LiveblocksRoomContext.Provider>
  )
}

function getRoomId(data: ExistingDocumentData) {
  const { version, meta } = data

  const defaultRoomId = getDraftRoomId(meta.id)

  switch (version.status) {
    case "unclassified":
    case "published":
    case "archived":
      return defaultRoomId
    case "release":
      return `${defaultRoomId}/${version.release_id}`
  }
}

function getDraftRoomId(metaId: string) {
  return `${getRepositoryName()}/${metaId}`
}

const aiAuthor = {
  id: "ai@prismic.io",
  email: "ai@prismic.io",
  firstname: "AI",
  lastname: "Assistant",
}

interface UseCollaborationStorageArgs {
  /**
   * Supply the document version status directly to determine if collaboration data should be used.
   * This way we aren't dependent on URL search params or what is in the storage, as it may be empty.
   */
  status: DocumentVersionStatus
}

/**
 * Similar to Liveblock's useStorage but optional and not throwing.
 * Indeed, the presence of a room above the component hierarchy is optional for Page Builder.
 */
export function useCollaborationStorage(args: UseCollaborationStorageArgs) {
  const { status } = args

  const goToDocument = useGoToDocument()
  const canUseCollaborationData = useCollaborationDataFlag() === "on"

  const { shortId } = useProfile()

  // useStorage may return null when it loads and we prefer to return undefined
  const value = useMaybeStorage((storageObject) => storageObject) ?? undefined

  // useCollaborationStorage can be rendered both in a new and in an existing document page
  // so we need to handle the case where the documentId is not present
  // but whenever the useCollaborationStorage hook will be used, the documentId will be present
  const { path: routeParams } = useRouteParams({
    path: { documentId: z.string().optional() },
  })

  const updateStorage = useMaybeLiveblocksMutation(
    ({ storage }, localData: UpdateStorageObject) => {
      if (storage.get("schemaVersion") === SCHEMA_VERSION) {
        handleDocumentStorageChanges({ storage, localData })

        storage.update({
          ...mergeChangesIntoStorageObject({ ...localData, shortId }),
          lastModifiedDate: new Date().toISOString(),
          commitHash: env.PRISMIC_GIT_COMMIT_HASH,
        })
      } else {
        // If the collaboration storage is on an outdated schema version,
        // we migrate it to a new schema on the first update
        storage.update({
          ...mergeChangesIntoStorageObject(
            {
              ...storage.toObject(),
              ...localData, // allow any new changes to be included while updating the version
              shortId,
            },
            true, // allow document to be overwritten as the current storage object is not compatible with the new one
          ),
          lastModifiedDate: new Date().toISOString(),
          schemaVersion: SCHEMA_VERSION,
          commitHash: env.PRISMIC_GIT_COMMIT_HASH,
        })
      }
    },
    [shortId],
  )

  // To avoid race condition when creating a document version we must operate on the most up to date status.
  // The status from the URL coming from react-router is delayed so I cannot rely on it.
  const [optimisticStatus, setOptimisticStatus] =
    useState<DocumentVersionStatus>(status)

  useOnChange(status, (newStatus) => {
    setOptimisticStatus(newStatus)
  })

  const { mutate: saveDraftVersion, isPending: isSavingDraftVersion } =
    useMutation({
      mutationFn: ({
        localData,
        documentId,
      }: {
        localData: UpdateStorageObject
        documentId: string
      }) =>
        maybeCreateDraftVersion({
          documentId,
          localData,
        }),
    })

  /**
   * Update function that updates drafts/releases storage
   * and redirects from published/archived to draft while handling storage changes.
   */
  const updateAndMaybeRedirect = useCallback(
    (localData: UpdateStorageObject) => {
      if (isDraftOrRelease(optimisticStatus)) {
        updateStorage(localData)
        return
      }

      // We don't want any RTC related saves/redirects from published/archived versions
      // when Collaboration Data is off.
      if (!canUseCollaborationData) {
        return
      }

      // A draft version of a document is needed if it doesn't yet exist,
      // because drafts appear in UI only after we can retrieve them from Prismic DB.
      if (!isSavingDraftVersion) {
        const documentId = routeParams.documentId
        if (!documentId) {
          // This should never happen as the documentId will be present,
          // as we preemptively save the doc on creation before you're able to edit it
          throw new Error(
            "documentId is required to be present in URL when performing edits with collaboration room enabled",
          )
        }

        saveDraftVersion(
          { documentId, localData },
          {
            onSuccess() {
              setOptimisticStatus("unclassified")
              goToDocument({
                status: "unclassified",
                id: documentId,
                replace: false,
              })
            },
          },
        )
      }

      updateStorage({
        document: localData.document,
        tags: localData.tags,
        status: "unclassified",
      })
    },
    [
      optimisticStatus,
      canUseCollaborationData,
      isSavingDraftVersion,
      updateStorage,
      saveDraftVersion,
      goToDocument,
      routeParams.documentId,
    ],
  )

  const isInsideRoom = useIsInsideRoom()
  if (!isInsideRoom) {
    return undefined
  }

  // If we can't use collaboration data, we don't want to use room data
  // If the document is a draft or release, we want to use room data
  // Otherwise, we don't want to use room data and only show Prismic DB data
  const collaborationDraftOrReleaseValue =
    canUseCollaborationData && isDraftOrRelease(status) ? value : undefined

  return {
    value: collaborationDraftOrReleaseValue,
    updateAndMaybeRedirect,
  }
}

/**
 * Liveblocks' useStorage but does not throw when it's not inside a RoomProvider
 * (which happens when `pb-collaboration-room` flag is off)
 */
const useMaybeStorage: typeof useStorage = (selector, isEqual) => {
  try {
    return useStorage(selector, isEqual)
  } catch {
    return null
  }
}

/**
 * Liveblocks' useMutation but does not throw when it's not inside a RoomProvider
 * (which happens when `pb-collaboration-room` flag is off)
 */
const useMaybeLiveblocksMutation: typeof useLiveblocksMutation = (
  callback,
  deps,
) => {
  try {
    return useLiveblocksMutation(callback, deps)
  } catch {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-explicit-any
    return (() => {}) as any
  }
}

interface UseCollaborativeCommentsArgs {
  field?: string
  initialContent?: Content
}

export function useCollaborativeComments(
  args: UseCollaborativeCommentsArgs,
): Extension {
  const extension = useLiveblocksExtension({
    comments: true,
    mentions: false, // A tricky bug occurs when mentions are enabled and the content includes an '@' symbol—the editor crashes because it attempts to unregister the Mention plugin.
    ...args,
  })

  return extension
}

export interface UpdateStorageObject {
  document?: Liveblocks["Storage"]["document"] | DocumentT
  title?: string
  status?: DocumentVersionStatus
  shortId?: string
  releaseId?: string
  tags?: LiveList<string>
  contentId?: string
  lastModifiedDate?: string
}

function mergeChangesIntoStorageObject(
  changes: UpdateStorageObject,
  overrideDocument?: boolean,
) {
  return {
    ...(overrideDocument && changes.document
      ? {
          document: liveDocument(changes.document),
        }
      : {}),
    ...(changes.title ? { title: changes.title } : {}),
    ...(changes.status ? { status: changes.status } : {}),
    ...(changes.shortId ? { shortId: changes.shortId } : {}),
    ...(changes.releaseId ? { releaseId: changes.releaseId } : {}),
    ...(changes.contentId ? { contentId: changes.contentId } : {}),
    ...(changes.tags
      ? { tags: wrapWithLiveblocksModel(changes.tags) as LiveList<string> }
      : {}),
    ...(changes.lastModifiedDate
      ? { lastModifiedDate: changes.lastModifiedDate }
      : {}),
  }
}

function liveDocument(document: UpdateStorageObject["document"]) {
  return wrapWithLiveblocksModel(document) as LiveObject<LsonObject>
}

interface MaybeCreateDocumentArgs {
  documentId: string
  localData: UpdateStorageObject
}

/**
 * Creates a draft version in Prismic DB if it doesn't exist.
 *
 * @param args - The arguments for creating a document.
 */
async function maybeCreateDraftVersion(args: MaybeCreateDocumentArgs) {
  const { documentId, localData } = args

  const meta = await getDocumentById(
    baseUrl,
    env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
    documentId,
  )
  // Get the most recent version of the current document
  const version = findDocumentVersion({
    meta,
    releaseId: undefined,
    status: "unclassified",
    archivedVersionId: undefined,
  })

  // Combine the version and local data
  // This is needed to transfer changes from the published document to the
  // newly created draft document
  const versionWithLocalChanges = {
    ...version,
    ...localData,
  }

  if (version.status !== "unclassified") {
    await saveDocument({
      id: meta.id,
      baseVersionId: undefined,
      releaseId: undefined,
      documentTags: new Set(versionWithLocalChanges.tags),
      content: versionWithLocalChanges.document as unknown as DocumentT,
      status: "unclassified",
    })

    void revalidateDocumentMeta(meta.id)
  }
}

declare global {
  interface Liveblocks {
    Storage: Partial<LiveblocksStorageType> | LiveblocksStorageType

    RoomEvent:
      | { type: "autosave"; state: "pending" }
      | { type: "autosave"; state: "success" }
      | { type: "autosave"; state: "error" }

    UserMeta: {
      id?: string
      info?: {
        /**
         * Liveblocks specific field, representing the full user name.
         *
         * Must be present for TipTap plugin
         */
        name?: string
        /**
         * Liveblocks specific field, representing the attached user color.
         *
         * Must be present for TipTap plugin.
         */
        color?: string
        avatar?: string | undefined
        firstname?: string
        lastname?: string
        email?: string
        themeColor?: "indigo9" | "purple9" | "green9" | "amber9" | "grey9"
      }
    }
  }
}

type LiveblocksStorageType = {
  document: LiveObject<LsonObject>
  title: string | undefined
  status: DocumentVersionStatus
  shortId: string
  releaseId: string | undefined
  tags: LiveList<string>
  repository: string
  documentId: string
  schemaVersion: typeof SCHEMA_VERSION | undefined
  commitHash: string | undefined
  contentId: string | undefined
  lastModifiedDate: string | undefined
}

const SCHEMA_VERSION = 3

function isPublishedOrArchived(
  status: DocumentVersionStatus,
): status is "published" | "archived" {
  return status === "published" || status === "archived"
}

function isDraftOrRelease(
  status: DocumentVersionStatus,
): status is "unclassified" | "release" {
  return status === "unclassified" || status === "release"
}
